import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { UserMenu } from '../components/auth/UserMenu';
import { Sparkles, Zap, Download, Layout, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Layout className="w-6 h-6" />,
      title: "Smart Layouts",
      description: "Automatically calculates optimal badge placement on A4 paper"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Create professional badge designs in seconds, not hours"
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Print Ready PDFs",
      description: "Export high-quality PDFs with precise cutting guides"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Multiple Sizes",
      description: "Round, square, and rectangle badges in various dimensions"
    }
  ];

  const badgeSizes = [
    { label: "Round", sizes: ["44mm", "58mm"] },
    { label: "Square", sizes: ["50x50mm"] },
    { label: "Rectangle", sizes: ["80x53mm", "90x65mm"] }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-playful">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold gradient-text">Badgeify</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/maker')}
              className="rounded-2xl bg-gradient-to-r from-primary to-secondary hover:shadow-hover transition-all duration-300 hover:scale-105"
              size="lg"
            >
              Start Creating <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border/50 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">Professional Badge Design Made Simple</span>
          </div>

          <h2 className="text-5xl md:text-7xl font-bold leading-tight">
            Create <span className="gradient-text">Beautiful Badges</span> in Minutes
          </h2>

          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Design professional badges with precise measurements, automatic layouts, and print-ready PDFs. Perfect for events, conferences, and teams.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <Button
              onClick={() => navigate('/maker')}
              size="lg"
              className="rounded-2xl text-lg px-8 py-6 bg-gradient-to-r from-primary to-secondary hover:shadow-hover transition-all duration-300 hover:scale-105 min-w-[200px]"
            >
              <Zap className="mr-2 w-5 h-5" />
              Badge Maker
            </Button>
            <Button
              onClick={() => navigate('/polaroid-maker')}
              size="lg"
              className="rounded-2xl text-lg px-8 py-6 bg-gradient-to-r from-pink-500 to-rose-500 hover:shadow-hover transition-all duration-300 hover:scale-105 min-w-[200px] text-white border-0"
            >
              <Sparkles className="mr-2 w-5 h-5" />
              Polaroid Maker
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">Everything You Need</h3>
          <p className="text-xl text-muted-foreground">Powerful features for professional badge making</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="card-playful p-6 bg-card border-border/50 hover:border-primary/50 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4 text-primary">
                {feature.icon}
              </div>
              <h4 className="font-semibold text-lg mb-2">{feature.title}</h4>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Supported Sizes */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">All Badge Sizes Supported</h3>
          <p className="text-xl text-muted-foreground">Professional dimensions with precise cutting guides</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {badgeSizes.map((category, index) => (
            <Card
              key={index}
              className="card-playful p-6 bg-gradient-to-br from-card to-muted border-border/50"
            >
              <Badge className="mb-4 rounded-full bg-gradient-to-r from-primary to-secondary text-primary-foreground">
                {category.label}
              </Badge>
              <div className="space-y-2">
                {category.sizes.map((size, sizeIndex) => (
                  <div key={sizeIndex} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    <span className="font-medium">{size}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <Card className="card-playful max-w-4xl mx-auto p-12 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-2 border-primary/20 text-center">
          <h3 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Create <span className="gradient-text">Amazing Badges?</span>
          </h3>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of event organizers, print shops, and teams creating professional badges.
          </p>
          <Button
            onClick={() => navigate('/maker')}
            size="lg"
            className="rounded-2xl text-lg px-10 py-7 bg-gradient-to-r from-primary to-secondary hover:shadow-hover transition-all duration-300 hover:scale-105"
          >
            <Sparkles className="mr-2 w-5 h-5" />
            Start Creating Now
          </Button>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/50 backdrop-blur-sm mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-semibold gradient-text">Badgeify</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2024 Badgeify. Professional badge design made simple.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
